import {
  ArrowPathIcon,
  ArrowUpCircleIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import useAxios from "axios-hooks";
import { useEffect } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";

export default function ImportJob() {
  const params = useParams();

  const { setTitleComponent } = useOutletContext() as any;

  const [{ data, loading, error }, getJobs] = useAxios<any>(
    `/import-jobs/${params.jobId}`
  );

  useEffect(() => {
    setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        {/* <ArrowUpTrayIcon className="h-8 w-8" /> */}
        <Link
          to={"/import-jobs"}
          className="hover:underline hover:text-primary"
        >
          Import Jobs
        </Link>
        <div>/</div>
        {!loading ? data?.name : <h1>Loading</h1>}
      </div>
    );
  }, [loading]);

  function downloadCSVFromFailedRecords(fail: any[], records: any[]) {
    const headers = [
      "username",
      "email",
      "temporary_password",
      "role",
      "send_invitation",
    ];
    const csvRows = [headers.join(",")];

    fail.forEach((failEntry) => {
      const record = records[failEntry.rowNumber];
      if (!record) return;

      const row = [
        record.username,
        record.email,
        record.temporary_password,
        record.role,
        record.send_invitation,
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "success-records.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const failedRowIndexes = new Set(data?.fail?.map((f: any) => f.rowNumber));
  const succededRowIndex = new Set(data?.success?.map((f: any) => f.rowNumber));

  return (
    <div className="p-4 space-y-6">
      {/* Job Summary */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h2>
            <button
              className="btn btn-xs btn-primary btn-outline"
              onClick={() => getJobs()}
            >
              <ArrowPathIcon className="h-4 w-4"></ArrowPathIcon> REFRESH
            </button>
          </h2>
          <h2 className="card-title text-xl font-semibold">{data?.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <span className="font-medium text-gray-600">Status:</span>{" "}
              <span
                className={`badge ${
                  data?.status === "CREATED" ? "badge-info" : ""
                } ${data?.status === "IN_PROGRESS" ? "badge-warning" : ""} ${
                  data?.status === "FINISHED" ? "badge-success" : ""
                }`}
              >
                {data?.status}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Created At:</span>{" "}
              {new Date(data?.createdAt).toLocaleString()}
            </div>
            <div className="col-span-full">
              <span className="font-medium text-gray-600">Classrooms:</span>
              <ul className="list-disc list-inside space-y-1 mt-1">
                {data?.classrooms?.map((cls: any) => (
                  <li key={cls._id} className="ml-2">
                    <span className="font-semibold">{cls.name}</span> -{" "}
                    <span className="text-sm text-gray-600">ID: {cls._id}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Failed Records */}
      {data?.fail?.length > 0 && (
        <div className="card border border-error bg-error/10 shadow-md">
          <div className="card-body">
            <h2 className="card-title text-error">
              {data.fail.length} record(s) failed to import
            </h2>

            {/* Download Button */}
            <button
              onClick={() =>
                downloadCSVFromFailedRecords(data.fail, data.records)
              }
              className="btn btn-sm btn-outline btn-error w-fit"
            >
              Download Failed Records as CSV
            </button>

            {/* Failed Records Table */}
            <div className="overflow-x-auto mt-4">
              <table className="table table-sm  w-full">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Row</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Error Message</th>
                  </tr>
                </thead>
                <tbody>
                  {data.fail.map((fail: any, idx: number) => {
                    const record = data.records?.[fail.rowNumber];
                    return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{fail.rowNumber + 1}</td>
                        <td>{record?.username || "N/A"}</td>
                        <td>{record?.email || "N/A"}</td>
                        <td>
                          <span className="badge badge-outline">
                            {record?.role || "N/A"}
                          </span>
                        </td>
                        <td className="text-red-600">{fail.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Records Table */}
      <div className="overflow-x-auto">
        {data?.success?.length > 0 && (
          <div className="flex">
            <h2 className="card-title text-xl font-semibold">
              {data?.records?.length} record(s) imported
            </h2>
            <button
              onClick={() =>
                downloadCSVFromFailedRecords(data.success, data.records)
              }
              className="btn btn-sm btn-outline btn-success w-fit ml-auto"
            >
              Download Successfull Records as CSV
            </button>
          </div>
        )}

        <table className="table w-full">
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              <th>Username</th>
              <th>Email</th>
              <th>Temporary Password</th>
              <th>Send Invitation</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {data?.records?.map((record: any, idx: number) => {
              const isFailed = failedRowIndexes.has(idx);
              const isSuccess = succededRowIndex.has(idx);

              return (
                <tr
                  key={idx}
                  className={`
                     ${
                       isFailed
                         ? "bg-red-50 dark:bg-red-900/50 dark:text-red-200"
                         : ""
                     }
                      ${
                        isSuccess
                          ? "bg-green-50 dark:bg-green-900/50 dark:text-green-200"
                          : ""
                      }
                  }`}
                >
                  <td>{idx + 1}</td>
                  <td>
                    {isFailed && (
                      <span className="tooltip" data-tip="This record failed">
                        <span className="badge badge-error text-white">
                          Failed
                        </span>
                      </span>
                    )}
                    {isSuccess && (
                      <span
                        className="tooltip"
                        data-tip="This record instered succesfully"
                      >
                        <span className="badge badge-success text-white">
                          Succeeded
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="flex items-center gap-2">{record.username}</td>
                  <td>{record.email}</td>
                  <td>
                    <code>{record.temporary_password}</code>
                  </td>
                  <td>
                    {record.send_invitation === "true" ? (
                      <span className="badge badge-outline badge-primary">
                        Yes
                      </span>
                    ) : (
                      <span className="badge badge-outline badge-error">
                        No
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="badge badge-outline badge-primary">
                      {record.role}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
